import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate, useParams } from 'react-router';
import { graphql } from '@/gql';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { SortableList } from '@/components/ui/SortableList';
import { Switch } from '@/components/ui/Switch';
import { FormField, FormLayout, FormSection } from '@/components/ui/FormLayout';

const CategoryDoc = graphql(/* GraphQL */ `
  query Category($id: ID!) {
    category(id: $id) {
      id
      name
      isActive
      showInMenu
      isDoor
      products {
        id
        name
      }
    }
    products(includeInactive: false, pageSize: 500) {
      nodes {
        id
        name
        categories {
          id
        }
      }
    }
  }
`);

const UpdateCategoryDoc = graphql(/* GraphQL */ `
  mutation UpdateCategoryDetails($id: ID!, $input: UpdateCategoryInput!) {
    updateCategory(id: $id, input: $input) {
      id
      name
      isActive
      showInMenu
      isDoor
    }
  }
`);

const SetCategoryProductsDoc = graphql(/* GraphQL */ `
  mutation SetCategoryProducts($id: ID!, $productIds: [ID!]!) {
    setCategoryProducts(id: $id, productIds: $productIds) {
      id
    }
  }
`);

const ReorderProductCategoryDoc = graphql(/* GraphQL */ `
  mutation ReorderProductCategory(
    $entity: SortableEntity!
    $orderedIds: [ID!]!
    $parentId: ID
  ) {
    reorder(entity: $entity, orderedIds: $orderedIds, parentId: $parentId)
  }
`);

interface ProductItem {
  id: string;
  name: string;
}

export function CategoryEditPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, loading, refetch } = useQuery(CategoryDoc, {
    variables: { id },
  });
  const [updateCategory, { loading: savingDetails }] = useMutation(UpdateCategoryDoc);
  const [setCategoryProducts, { loading: savingProducts }] = useMutation(
    SetCategoryProductsDoc,
  );
  const [reorder] = useMutation(ReorderProductCategoryDoc);

  const category = data?.category;
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [showInMenu, setShowInMenu] = useState(true);
  const [isDoor, setIsDoor] = useState(false);
  const [assigned, setAssigned] = useState<ProductItem[]>([]);
  const [availableSearch, setAvailableSearch] = useState('');
  const [seededFor, setSeededFor] = useState<string | null>(null);

  if (category && category.id !== seededFor) {
    setSeededFor(category.id);
    setName(category.name);
    setIsActive(category.isActive);
    setShowInMenu(category.showInMenu);
    setIsDoor(category.isDoor);
    setAssigned(
      category.products.map((p) => ({ id: p.id, name: p.name })),
    );
  }

  const allProducts: ProductItem[] = useMemo(
    () =>
      (data?.products.nodes ?? []).map((p) => ({ id: p.id, name: p.name })),
    [data],
  );

  const assignedIds = useMemo(
    () => new Set(assigned.map((p) => p.id)),
    [assigned],
  );
  const available = useMemo(
    () => allProducts.filter((p) => !assignedIds.has(p.id)),
    [allProducts, assignedIds],
  );
  const filteredAvailable = useMemo(() => {
    const q = availableSearch.trim().toLowerCase();
    if (!q) return available;
    return available.filter((p) => p.name.toLowerCase().includes(q));
  }, [available, availableSearch]);

  const handleSaveDetails = async (event: React.FormEvent) => {
    event.preventDefault();
    if (savingDetails) return;
    await updateCategory({
      variables: { id, input: { name, isActive, showInMenu, isDoor } },
    });
    await refetch();
  };

  const handleSaveProducts = async () => {
    if (savingProducts) return;
    await setCategoryProducts({
      variables: { id, productIds: assigned.map((p) => p.id) },
    });
    await refetch();
  };

  const handleReorder = async (orderedIds: string[]) => {
    const byId = new Map(assigned.map((p) => [p.id, p]));
    const next = orderedIds
      .map((pid) => byId.get(pid))
      .filter((p): p is ProductItem => Boolean(p));
    setAssigned(next);
    await reorder({
      variables: {
        entity: 'PRODUCT_CATEGORY',
        orderedIds,
        parentId: id,
      },
    });
  };

  const assign = (product: ProductItem) => {
    setAssigned((current) => [...current, product]);
  };
  const unassign = (product: ProductItem) => {
    setAssigned((current) => current.filter((p) => p.id !== product.id));
  };

  if (loading && !data) {
    return <p className="text-sm text-gray-500">Loading category…</p>;
  }
  if (!category) {
    return <p className="text-sm text-gray-500">Category not found.</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate('/categories')}
          >
            Back
          </Button>
          <h1 className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
            {category.name}
          </h1>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <FormLayout
          title="Details"
          onSubmit={handleSaveDetails}
          footer={
            <Button type="submit" disabled={savingDetails}>
              {savingDetails ? 'Saving…' : 'Save details'}
            </Button>
          }
        >
          <FormSection title="Category">
            <FormField label="Name" required>
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
              />
            </FormField>
            <FormField label="Show in POS menu">
              <Switch
                checked={showInMenu}
                onChange={(event) => setShowInMenu(event.target.checked)}
                label={showInMenu ? 'Visible' : 'Hidden'}
              />
            </FormField>
            <FormField label="Active">
              <Switch
                checked={isActive}
                onChange={(event) => setIsActive(event.target.checked)}
                label={isActive ? 'Active' : 'Inactive'}
              />
            </FormField>
            <FormField
              label="Door ticket sales"
              description="When on, this category shows one extra button per door ticket phase for any night whose doors are currently open. Tickets go into the normal basket alongside drinks."
            >
              <Switch
                id="admin.categories.isDoor"
                checked={isDoor}
                onChange={(event) => setIsDoor(event.target.checked)}
                label={isDoor ? 'On' : 'Off'}
              />
            </FormField>
          </FormSection>
        </FormLayout>

        <Card
          title="Products"
          subtitle="Drag rows on the right to order them inside the category."
          actions={
            <Button
              id="admin.categories.saveProducts"
              onClick={handleSaveProducts}
              disabled={savingProducts}
            >
              {savingProducts ? 'Saving…' : 'Save products'}
            </Button>
          }
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <h3 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                Available ({filteredAvailable.length})
              </h3>
              <Input
                id="admin.categories.searchAvailable"
                type="search"
                value={availableSearch}
                onChange={(event) => setAvailableSearch(event.target.value)}
                placeholder="Search available products…"
                className="mb-2"
              />
              <div className="max-h-96 overflow-y-auto rounded-xl border border-gray-200 dark:border-white/10">
                {filteredAvailable.length === 0 ? (
                  <p className="p-4 text-xs text-gray-500">
                    {available.length === 0
                      ? 'Every active product is already assigned.'
                      : 'No matching products.'}
                  </p>
                ) : (
                  <ul className="divide-y divide-gray-100 dark:divide-white/10">
                    {filteredAvailable.map((p) => (
                      <li
                        key={p.id}
                        className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
                      >
                        <span className="truncate text-gray-700 dark:text-gray-200">
                          {p.name}
                        </span>
                        <Button
                          id={`admin.categories.assign.${p.name}`}
                          variant="ghost"
                          size="sm"
                          rightIcon={
                            <ChevronRight className="h-4 w-4" aria-hidden="true" />
                          }
                          onClick={() => assign(p)}
                        >
                          Add
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div>
              <h3 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                In this category ({assigned.length})
              </h3>
              <div className="max-h-96 overflow-y-auto rounded-xl border border-gray-200 p-2 dark:border-white/10">
                {assigned.length === 0 ? (
                  <p className="p-4 text-xs text-gray-500">
                    No products assigned yet.
                  </p>
                ) : (
                  <SortableList
                    items={assigned}
                    onReorder={handleReorder}
                    renderItem={(item, { dragHandle }) => (
                      <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-2 text-sm dark:border-white/10 dark:bg-gray-900">
                        {dragHandle}
                        <span className="flex-1 truncate text-gray-700 dark:text-gray-200">
                          {item.name}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={
                            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                          }
                          onClick={() => unassign(item)}
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                  />
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
